import React, { useMemo } from 'react';
import {
  MdArrowBack, MdHomeWork, MdPayments, MdPercent, MdEventAvailable,
  MdReceipt, MdChat, MdInfoOutline,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp, maskAmount } from '../contexts/AppContext';
import { denormalizeTransactions } from '../utils/denormalizeTransactions';

const fmtCad = (v) =>
  v == null ? '—'
    : '$' + Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCadInt = (v) =>
  v == null ? '—'
    : '$' + Math.round(Number(v)).toLocaleString('en-CA');

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Renewal urgency: < 90 days = amber, < 30 days = red.
const renewalChip = (iso) => {
  if (!iso) return null;
  const today = new Date();
  const renew = new Date(iso);
  const days  = Math.round((renew - today) / 86400000);
  if (days <= 30)  return { label: `${days}d to renewal`, tint: 'var(--td-red)' };
  if (days <= 90)  return { label: `${days}d to renewal`, tint: 'var(--td-amber)' };
  if (days <= 365) return { label: `${days}d to renewal`, tint: 'var(--td-teal)' };
  return null;
};

const MortgageDetailScreen = ({ onNavigate, onBack, onChatHandoff }) => {
  const { loadedProfileData } = useProfile();
  const { hideBalances } = useApp();
  const mortgage = loadedProfileData?.profile?.accounts?.mortgage;

  const payments = useMemo(() => {
    if (!mortgage?.transactions) return [];
    return denormalizeTransactions(mortgage.transactions)
      .filter(t => t.amount && t.amount < 0)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 6);
  }, [mortgage]);

  // Empty state when the active profile has no mortgage. We point at the
  // application flow rather than just showing a blank screen.
  if (!mortgage) {
    return (
      <div className="screen bh-flow">
        <header className="bh-flow-hero">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Mortgage</h1>
          </div>
          <MdHomeWork size={22} style={{ color: 'var(--bh-black)' }} />
        </header>
        <div className="bh-flow-body" style={{ alignItems: 'center', textAlign: 'center' }}>
          <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-sm)' }}>
            <MdHomeWork size={48} color="var(--bh-gray)" />
            <div style={{ font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>No mortgage on file</div>
            <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
              We don't see a mortgage account for the active profile. Browse our mortgage products to learn more.
            </p>
            <button className="bh-btn-primary" onClick={() => onNavigate('mortgages')}>
              See mortgage products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const principal     = mortgage.original_principal;
  const balance       = mortgage.current_balance ?? mortgage.current_balance_owing ?? mortgage.current_value ?? mortgage.balance;
  const paid          = principal != null && balance != null ? principal - balance : null;
  const paidPct       = principal && paid ? Math.max(0, Math.min(1, paid / principal)) : 0;
  const chip          = renewalChip(mortgage.renewal_date);
  const askIvaAbout   = `I'd like to talk about my mortgage. The current balance is ${fmtCad(balance)} at ${mortgage.interest_rate}, renewing ${fmtDate(mortgage.renewal_date)}. What should I be thinking about as renewal approaches?`;

  return (
    // Same shell as AccountDetailsScreen so the styling matches the rest of
    // the accounts area: tall green hero with a white balance card inside,
    // then the detail rows in the body. No premium-dark gradient here.
    <div className="screen account-details-screen">
      <div className="scroll-content account-details-scroll">
        <header className="bh-flow-hero tall">
          <div className="bh-flow-hero-row">
            <div className="bh-flow-hero-left">
              <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back">
                <MdArrowBack />
              </button>
              <h1 className="bh-flow-title">Mortgage</h1>
            </div>
          </div>

          <div className="bh-balance-card">
            <p className="bh-balance-card-label">Balance Remaining</p>
            <p className="bh-balance-card-amount">{maskAmount(fmtCadInt(balance), hideBalances)}</p>
            {mortgage.property_address && (
              <p style={{ margin: 'var(--space-2xs) 0 0', font: 'var(--bh-body-sm)', opacity: 0.8 }}>
                {mortgage.property_address}
              </p>
            )}
            {chip && (
              <span style={{
                alignSelf: 'flex-start', marginTop: 'var(--space-2xs)',
                background: chip.tint, color: 'var(--bh-white)',
                padding: '4px 10px', font: 'var(--bh-caption)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                borderRadius: 'var(--radius-full)',
              }}>
                {chip.label}
              </span>
            )}
          </div>
        </header>

        <div className="account-details-content">

        {/* Progress against original principal */}
        {principal != null && (
          <div className="bh-form-card" style={{ gap: 'var(--space-2xs)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
              <span style={{ fontWeight: 700, color: 'var(--bh-black)' }}>{paid != null ? `${fmtCadInt(paid)} paid` : '—'}</span>
              <span>of {fmtCadInt(principal)}</span>
            </div>
            <div style={{ width: '100%', height: 8, background: 'var(--bh-light-gray)', overflow: 'hidden', borderRadius: 'var(--radius-full)' }}>
              <div style={{
                width: `${paidPct * 100}%`, height: '100%',
                background: 'var(--td-green)',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )}

        {/* Key facts */}
        <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
          {[
            { Icon: MdPercent,         label: 'Interest rate',   value: `${mortgage.interest_rate || '—'}${mortgage.rate_type ? ` · ${mortgage.rate_type}` : ''}`, tint: 'var(--td-amber)' },
            { Icon: MdPayments,        label: 'Monthly payment', value: fmtCad(mortgage.monthly_payment), tint: 'var(--td-green)' },
            { Icon: MdEventAvailable,  label: 'Next payment',    value: fmtDate(mortgage.next_payment_date), tint: 'var(--td-blue)' },
            { Icon: MdEventAvailable,  label: 'Renewal date',    value: fmtDate(mortgage.renewal_date),     tint: 'var(--td-purple)' },
            { Icon: MdReceipt,         label: 'Amortization remaining', value: mortgage.amortization_remaining_years != null ? `${mortgage.amortization_remaining_years} years` : '—', tint: 'var(--td-brown)' },
            { Icon: MdHomeWork,        label: 'Original principal', value: fmtCad(principal), tint: 'var(--td-teal)' },
          ].map((row, i, arr) => (
            <div
              key={row.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                padding: 'var(--space-sm)',
                borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--bh-light-gray)',
              }}
            >
              <span style={{
                width: 36, height: 36, background: row.tint, color: 'var(--bh-white)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, borderRadius: 'var(--radius-md)',
              }}>
                <row.Icon size={18} />
              </span>
              <span style={{ flex: 1, font: 'var(--bh-body)', color: 'var(--bh-black)' }}>{row.label}</span>
              <span style={{ font: 'var(--bh-body)', fontWeight: 700, color: 'var(--bh-black)' }}>{row.value}</span>
            </div>
          ))}
        </div>

        {/* Recent payments */}
        {payments.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Recent payments</h2>
            <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
              {payments.map((p, i) => (
                <div
                  key={p.id || p.date}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                    padding: 'var(--space-sm)',
                    borderBottom: i === payments.length - 1 ? 'none' : '1px solid var(--bh-light-gray)',
                  }}
                >
                  <span style={{
                    width: 36, height: 36, background: 'var(--td-premium)', color: 'var(--td-gold)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, borderRadius: 'var(--radius-md)',
                  }}>
                    <MdPayments size={18} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: 'var(--bh-body)', fontWeight: 600, color: 'var(--bh-black)' }}>
                      {p.description || 'Mortgage payment'}
                    </div>
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                      {fmtDate(p.date)}
                    </div>
                  </div>
                  <span style={{ font: 'var(--bh-body)', fontWeight: 700, color: 'var(--td-red)' }}>
                    {fmtCad(p.amount)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="bh-info-box">
          <MdInfoOutline size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Renewal notices arrive 4 months before your term ends. You can lock in a new rate up to 120 days early without a fee.
        </div>

        <div className="bh-btn-row">
          <button
            className="bh-btn-outline-dark"
            style={{ flex: 1 }}
            onClick={() => {
              if (onChatHandoff) onChatHandoff(askIvaAbout);
              else onNavigate('chat');
            }}
          >
            <MdChat size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Ask IVA
          </button>
          <button
            className="bh-btn-primary"
            style={{ flex: 1 }}
            onClick={() => onNavigate('mortgages')}
          >
            Renewal options
          </button>
        </div>
        </div>{/* /.account-details-content */}
      </div>{/* /.scroll-content */}
    </div>
  );
};

export default MortgageDetailScreen;
